import type { StateCreator } from "zustand";
import type { DBOpsState } from "../useStudioAPI/hooks/dbOps.store";
import { instantiateMegaJSONLookupWorker } from "../processLookup";
import type {
  MegaJSONLookupData,
  MetaJSONLookupData
} from "../processLookup/processLookup.types";
import { workerMessageQueue } from "../processLookup/workerMessageQueue";
import type { WorkerInstance } from "../processLookup/createLookupWorker";

export type LookupWorkerSlice = {
  megaJSONWorkerInstance: WorkerInstance | null;
  megaJSONLookUpData: MegaJSONLookupData;
  isECAEnabled?: boolean;
  isFlowModeEnabled?: boolean;

  initializeMegaJSONWorker: (isReadOnly: boolean) => void;
  terminateMegaJSONWorker: () => void;
  getMegaJSONLookup: () => MegaJSONLookupData;
  getMetaJSONLookup: (processId: string) => MetaJSONLookupData;
  updateMetaJSONLookUp: (megaJSONLookupData: MegaJSONLookupData) => void;
};

export const emptyMetaJSONLookupData: MetaJSONLookupData = {
  componentTagToIdMapping: {},
  ruleIndicatorLookup: {
    upstream: {},
    downstream: {}
  },
  waterfallView: { steps: [], swimlanes: [] },
  flowLines: [],
  metaJSONLookup: {
    fields: {},
    steps: {},
    swimlanes: {},
    rules: {},
    process: {},
    type: "normal"
  }
};

const initialState = {
  isECAEnabled: false,
  isFlowModeEnabled: false,
  megaJSONWorkerInstance: null,
  megaJSONLookUpData: {} as MegaJSONLookupData
};

export const lookupWorkerSlice: StateCreator<
  DBOpsState,
  [],
  [],
  LookupWorkerSlice
> = (set, get) => ({
  ...initialState,

  initializeMegaJSONWorker: (isReadOnly: boolean) => {
    const { megaJSONWorkerInstance, updateMetaJSONLookUp } = get();
    if (!megaJSONWorkerInstance) {
      const worker = instantiateMegaJSONLookupWorker(
        ({
          megaJSONLookupData,
          referencedAppSettings,
          shouldUpdateReferenceSettings,
          newBaseAppSettings
        }) => {
          updateMetaJSONLookUp(megaJSONLookupData);

          if (shouldUpdateReferenceSettings && !isReadOnly) {
            // Wrap in transaction to skip edit mode validation for background operations
            get().transaction(() => {
              get().replaceAllAppSettings(referencedAppSettings);
              get().setBaseAppSettings(newBaseAppSettings);
            });
          }

          // Process any queued messages after worker completes
          workerMessageQueue.processQueue(worker);
        }
      );
      set({ megaJSONWorkerInstance: worker });
    }
  },

  terminateMegaJSONWorker: () => {
    const { megaJSONWorkerInstance } = get();
    if (megaJSONWorkerInstance) {
      // Clear any pending messages before terminating
      workerMessageQueue.clearWorker(megaJSONWorkerInstance);
      megaJSONWorkerInstance.terminate();
      set({
        megaJSONWorkerInstance: null,
        megaJSONLookUpData: get().megaJSONLookUpData
      });
    }
  },

  getMegaJSONLookup: () => get().megaJSONLookUpData,
  getMetaJSONLookup: processId => {
    const megaJSONLookup = get().megaJSONLookUpData;
    const metaJSONLookup =
      megaJSONLookup?.[processId] || emptyMetaJSONLookupData;
    return metaJSONLookup;
  },
  updateMetaJSONLookUp: megaJSONLookUpData => set({ megaJSONLookUpData })
});
